<template>
  <div class="user-edit">
    <b-form>
      <b-form-group
        horizontal
        label="Email"
        :invalid-feedback="emailInvalidFeedback"
      >
        <b-form-input
          v-model="$v.email.$model"
          :state="validateState('email')"
        />
      </b-form-group>
        <b-form-group
                horizontal
                label="Username"
        >
            <b-form-input
                    v-model="$v.username.$model"
                    :state="validateState('username')"
            />
        </b-form-group>
      <b-form-group horizontal label="Disabled">
        <b-form-checkbox
          switch
          :disabled="!disabled && isCurrentAdminForm"
          v-model="disabled"
          :value="1"
          :unchecked-value="0"
        />
      </b-form-group>
      <b-form-group horizontal label="Role:">
        <b-form-radio-group
          id="role"
          v-model="$v.role.$model"
          name="role"
          :state="validateState('role')"
        >
          <div
            v-for="role in roleOptions"
            :key="role.value"
            class="d-inline-block"
          >
            <b-form-radio :value="role.value">
              {{ role.text }}
            </b-form-radio>
          </div>
          <b-form-invalid-feedback :state="validateState('role')">
            This field is required.
          </b-form-invalid-feedback>
        </b-form-radio-group>
      </b-form-group>
      <component
        ref="extraFieldsForm"
        :is="extraFieldsComponentName.name"
        v-bind="extraFieldsComponentName.props"
      >
        <b-form-group
          slot="email-input"
          label="Email"
          label-class="v2_font_size_20 v2_color_med_gray required"
          :invalid-feedback="emailInvalidFeedback"
        >
          <b-form-input
            id="email"
            class="rounded-0 v2_color_primary_navy"
            v-model="$v.email.$model"
            :state="validateState('email')"
          />
        </b-form-group>
      </component>
    </b-form>
    <hr />
    <new-password-form :user-id="parseInt(this.initialUser.id)" />
    <template v-if="isDemoMode">
      <hr />
      <div class="d-flex justify-content-end">
        <b-button @click="$emit('reset-user')" variant="primary">
          <template v-if="!resetLoading">Reset User</template>
          <template v-else>
            <b-spinner small />
            <span>Resetting...</span>
          </template>
        </b-button>
      </div>
    </template>
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import {
  ROLE_GM_ADMIN,
  ROLE_MSS_ADMIN,
  ROLE_MSS_RECRUITER,
  ROLE_MSS_USER, ROLE_NON_MSS_USER,
} from '@/helpers/rbacHelper';
import { validationMixin } from 'vuelidate';
import {email, minLength, required} from 'vuelidate/lib/validators';
import NewPasswordForm from '@/components/users/NewPasswordForm';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';
import AdminEditForm from '@/components/users/forms/AdminEditForm';
import MssUserEditForm from '@/components/users/forms/MssUserEditForm';
import RecruiterAdminProfile from '@/components/recruiter/admin/RecruiterAdminProfile';

export default {
  name: 'UserEdit',
  mixins: [validationMixin, GetMembershipTypeOptionsMixin],
  components: {
    NewPasswordForm,
    AdminEditForm,
    MssUserEditForm,
    RecruiterAdminProfile,
  },
  props: {
    initialUser: Object,
  },
  data: function () {
    const user = this.initialUser;
    let currRole = ROLE_MSS_USER;
    if (Array.isArray(user.roles)) {
      if (user.roles.includes(ROLE_GM_ADMIN)) {
        currRole = ROLE_GM_ADMIN;
      } else if (user.roles.includes(ROLE_MSS_ADMIN)) {
        currRole = ROLE_MSS_ADMIN;
      } else if (user.roles.includes(ROLE_MSS_RECRUITER)) {
        currRole = ROLE_MSS_RECRUITER;
      } else if (user.roles.includes(ROLE_NON_MSS_USER)) {
        currRole = ROLE_NON_MSS_USER;
      }
    }
    return {
      email: user.email || null,
      username: user.username || null,
      validated: false,
      saveLoading: false,
      resetLoading: false,
      membershipTypeId: user.society_membership_type_id
        ? parseInt(user.society_membership_type_id)
        : null,
      role: currRole,
      disabled: parseInt(user.disabled) || 0,
      society_id: parseInt(user.society_id),
    };
  },
  validations: function () {
    const validation = {
      email: {
        required,
        email,
      },
      username: {
        required,
        minLength: minLength(8),
      },
      role: {
        required,
      },
    };
    return validation;
  },
  computed: {
    emailInvalidFeedback: function () {
      return !this.$v.email.email
        ? 'Email must be a valid email.'
        : 'Email is required';
    },
    isAdminRoleSelected: function () {
      return this.role === ROLE_MSS_ADMIN || this.role === ROLE_GM_ADMIN;
    },
    roleOptions: function () {
      return this.$store.state.roleOptions;
    },
    isDemoMode: function () {
      return this.$store.getters.isDemoMode;
    },
    isRecruiterRoleSelected: function () {
      return this.role === ROLE_MSS_RECRUITER;
    },
    extraFieldsComponentName: function () {
      const adminUsersProps = {
        user: this.initialUser,
        role: this.role,
        societyMembershipTypeId: this.membershipTypeId,
      };
      if (this.isAdminRoleSelected) {
        return {
          name: AdminEditForm.name,
          props: adminUsersProps,
        };
      } else if (this.isRecruiterRoleSelected)
        return {
          name: RecruiterAdminProfile.name,
          props: { userId: this.initialUser.id },
        };
      return {
        name: MssUserEditForm.name,
        props: adminUsersProps,
      };
    },
    isCurrentAdminForm: function () {
      const user = this.$store.state.user;
      return user && parseInt(user.id) === parseInt(this.initialUser.id);
    },
    isEditedUserRecruiter: function () {
      const user = this.$store.state.user;
      return (
        user &&
        Array.isArray(user.roles) &&
        user.roles.includes(ROLE_MSS_RECRUITER)
      );
    },
  },
  methods: {
    getFormData: function () {
      this.$v.$touch();
      this.$refs.extraFieldsForm && this.$refs.extraFieldsForm.validate();
      if (this.$v.$anyError || (this.$refs.extraFieldsForm || {}).hasErrors()) {
        return;
      }
      const extraUserData = this.$refs.extraFieldsForm.getData();
      return {
        id: this.initialUser.id,
        roles: [this.role],
        disabled: this.disabled,
        ...extraUserData,
        email: this.email,
        username: this.username,
      };
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v[name];
      return $dirty ? !$error : null;
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.zip-input {
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }
}
</style>
